import {
  Button,
  Card,
  palette,
  pxToRem,
  Spacing,
  Text,
  Modal,
} from "@ottatech/design";
import React, { useState } from "react";
import styled from "styled-components";
import { useMutation } from "@apollo/client";

import {
  AccountSettings,
  AccountSettingsDocument,
  UnhideCompanyDocument,
} from "schema";

const CompanyCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CompanyLogo = styled.img`
  position: relative;
  width: auto;
  height: auto;
  max-height: ${pxToRem(40)};
  max-width: ${pxToRem(40)};
  object-fit: contian;
`;

const PopupCompanyLogoContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const PopupCompanyLogo = styled.img`
  position: relative;
  width: auto;
  height: auto;
  max-height: ${pxToRem(50)};
  object-fit: contian;
`;

const ModalText = styled(Text)`
  padding-top: ${pxToRem(20)};
`;

const CompanyText = styled(Text)`
  padding-left: ${pxToRem(10)};
`;

const Divider = styled.div`
  &::after {
    margin-top: ${pxToRem(10)};
    content: "";
    display: block;
    background-color: ${palette.grayscale.shade400};
    flex: 1;
    height: ${pxToRem(1)};
  }
`;

const UnhideButton = styled(Button)`
  border: none;
  text-decoration: underline;
  font-weight: bold;
  padding-top: 0;
  padding-bottom: 0;
  position
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-evenly;
  padding-bottom: ${pxToRem(20)};
  font-size: 0.8rem;
`;

const ActionButton = styled(Button)`
  width: ${pxToRem(170)};
`;

const LeftAlign = styled.div`
  display: flex;
  align-items: center;
`;
const RightAlign = styled.div``;

export function UnhideCompany({
  currentUser,
}: {
  currentUser: AccountSettings.CurrentUser;
}): React.ReactElement {
  const hiddenCompanies = (currentUser.hiddenCompanies ?? []).map(c => ({
    id: c.id,
    name: c.name,
    logoPath: c.logoPath ?? "",
  }));
  return (
    <Card style={{ marginTop: pxToRem(20) }}>
      <Spacing>
        <Text bold size={1} align="left">
          Hidden companies
        </Text>
        {hiddenCompanies.length > 1 ? (
          <Text size={0} align="left">
            You&apos;ve chosen to not see jobs from these companies
          </Text>
        ) : (
          <Text size={0} align="left">
            Any companies that you hide will appear here
          </Text>
        )}
        {hiddenCompanies.map(({ id, name, logoPath }, index) => (
          <Divider key={index}>
            <DisplayCompany
              key={index}
              id={id}
              name={name}
              logoPath={logoPath}
            />
          </Divider>
        ))}
      </Spacing>
    </Card>
  );
}

function DisplayCompany({
  id,
  name,
  logoPath,
}: {
  id: string;
  name: string;
  logoPath: string;
}): React.ReactElement {
  const [showHideModal, setShowHideModal] = useState(false);
  const handleClose = () => setShowHideModal(false);
  return (
    <CompanyCard>
      <LeftAlign>
        {logoPath != "" && <CompanyLogo src={logoPath} alt={`${name} logo`} />}
        <CompanyText bold size={0} align={"left"}>
          {name}
        </CompanyText>
      </LeftAlign>
      <RightAlign>
        <UnhideButton
          level="tertiary"
          onClick={() => setShowHideModal(true)}
          data-analytics-id="unhide-company"
        >
          Unhide
        </UnhideButton>
      </RightAlign>
      {showHideModal && (
        <Modal onClose={handleClose}>
          <UnhideComapny
            id={id}
            name={name}
            logoPath={logoPath}
            handleClose={handleClose}
          />
        </Modal>
      )}
    </CompanyCard>
  );
}

function UnhideComapny({
  id,
  name,
  logoPath,
  handleClose,
}: {
  id: string;
  name: string;
  logoPath: string;
  handleClose: () => void;
}): React.ReactElement {
  const [unhideCompanyMutation] = useMutation(UnhideCompanyDocument, {
    variables: {
      companyId: id,
    },
    refetchQueries: [{ query: AccountSettingsDocument }],
  });

  const handleClick = () => {
    unhideCompanyMutation();
    handleClose();
  };
  return (
    <Spacing>
      <ModalText align="center" size={1}>
        Do you want to see jobs from this company again?
      </ModalText>
      <PopupCompanyLogoContainer>
        {logoPath != "" && (
          <PopupCompanyLogo src={logoPath} alt={`${name} logo`} />
        )}
      </PopupCompanyLogoContainer>
      <ButtonContainer>
        <ActionButton
          level="primary"
          onClick={handleClick}
          data-testid="unhide-company"
        >
          Unhide
        </ActionButton>
        <ActionButton level="secondary" onClick={handleClose}>
          Keep Hiding
        </ActionButton>
      </ButtonContainer>
    </Spacing>
  );
}
